import { EnvelopeIcon, MagnifyingGlassIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiResponse } from "@/api/rest/core/types";
import { Account, AccountType, AssignmentEntityType } from "@/api/rest/models/account";
import { HouseholdWithOwnership } from "@/api/rest/models/household";
import { sendBuildingUserPasswordResetEmail, unassignAccountFromEntity } from "@/api/rest/services/accountService";
import { fetchAllHouseholds } from "@/api/rest/services/householdService";
import { Banner } from "@/components/common/Banner";
import Button from "@/components/common/Button";
import ConfirmationDialog from "@/components/common/ConfirmationDialog";
import Input from "@/components/common/Input";
import Spinner from "@/components/common/Spinner";
import UserManagement from "@/components/common/UserManagement";
import { naturallySortByTitle } from "@/utils/collection";

interface BuildingHomeownersDashboardProps {
  buildingId: number;
}

type PendingRemoval = {
  householdId: number;
  householdTitle: string;
  account: Account;
};

const dashboardQueryKey = (buildingId: number) => ["building-homeowners-dashboard", buildingId] as const;

export function BuildingHomeownersDashboard({ buildingId }: BuildingHomeownersDashboardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [expandedHouseholdId, setExpandedHouseholdId] = useState<number | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [passwordResetFeedback, setPasswordResetFeedback] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  const householdsQuery = useQuery({
    queryKey: dashboardQueryKey(buildingId),
    queryFn: async () => {
      const response = await fetchAllHouseholds();
      return unwrapApiResponse(response).filter((household) => household.buildingId === buildingId);
    },
    enabled: buildingId > 0,
    staleTime: 30 * 1000,
  });

  const removeMutation = useMutation({
    mutationFn: async ({ householdId, accountId }: { householdId: number; accountId: number }) => {
      unwrapApiResponse(await unassignAccountFromEntity(AssignmentEntityType.HOUSEHOLD, householdId, { accountId }));
    },
    onMutate: async ({ householdId, accountId }) => {
      setRemoveError(null);
      setPendingRemoval(null);
      await queryClient.cancelQueries({ queryKey: dashboardQueryKey(buildingId) });

      const previousHouseholds = queryClient.getQueryData<HouseholdWithOwnership[]>(dashboardQueryKey(buildingId));
      queryClient.setQueryData<HouseholdWithOwnership[]>(dashboardQueryKey(buildingId), (currentHouseholds) =>
        currentHouseholds?.map((household) =>
          household.id === householdId
            ? {
                ...household,
                accounts: household.accounts.filter((account) => account.id !== accountId),
              }
            : household,
        ),
      );

      return { previousHouseholds };
    },
    onError: (_, __, context) => {
      if (context?.previousHouseholds) {
        queryClient.setQueryData(dashboardQueryKey(buildingId), context.previousHouseholds);
      }
      setRemoveError(t("buildingSettings.homeownersDashboard.removeError"));
    },
    onSettled: async (_, __, variables) => {
      if (!variables) {
        await queryClient.invalidateQueries({ queryKey: dashboardQueryKey(buildingId) });
        return;
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: dashboardQueryKey(buildingId) }),
        queryClient.invalidateQueries({
          queryKey: ["accountAssignments", AssignmentEntityType.HOUSEHOLD, variables.householdId],
        }),
      ]);
    },
  });

  const passwordResetMutation = useMutation({
    mutationFn: async ({ userId, email }: { userId: number; email: string }) => {
      unwrapApiResponse(await sendBuildingUserPasswordResetEmail(buildingId, userId));
      return email;
    },
    onMutate: () => setPasswordResetFeedback(null),
    onSuccess: (email) =>
      setPasswordResetFeedback({
        message: t("buildingSettings.homeownersDashboard.passwordResetEmailSent", {
          email,
        }),
        success: true,
      }),
    onError: () =>
      setPasswordResetFeedback({
        message: t("buildingSettings.homeownersDashboard.passwordResetEmailFailed"),
        success: false,
      }),
  });

  const households = useMemo(() => naturallySortByTitle(householdsQuery.data ?? []), [householdsQuery.data]);

  const filteredHouseholds = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return households;
    }

    return households.filter((household) => {
      const accounts = getHouseholdAccounts(household);
      return (
        household.title.toLowerCase().includes(normalizedSearch) ||
        household.id.toString().includes(normalizedSearch) ||
        accounts.some((account) => getAccountSearchText(account).includes(normalizedSearch))
      );
    });
  }, [households, search]);

  const totalHomeownerAccounts = useMemo(
    () => households.reduce((count, household) => count + getHouseholdAccounts(household).length, 0),
    [households],
  );
  const householdsWithOwners = useMemo(
    () => households.filter((household) => getHouseholdAccounts(household).length > 0).length,
    [households],
  );

  const handleDashboardRefresh = async (householdId: number) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: dashboardQueryKey(buildingId) }),
      queryClient.invalidateQueries({ queryKey: ["accountAssignments", AssignmentEntityType.HOUSEHOLD, householdId] }),
    ]);
  };

  const handleHomeownerAdded = async (householdId: number) => {
    await handleDashboardRefresh(householdId);
    setExpandedHouseholdId(null);
  };

  if (householdsQuery.isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex items-center justify-center py-8">
          <Spinner className="h-6 w-6" />
        </div>
      </div>
    );
  }

  if (householdsQuery.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {t("buildingSettings.homeownersDashboard.loadError")}
      </div>
    );
  }

  return (
    <section className="mt-6 rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          <h3 className="text-lg font-semibold text-gray-900">{t("buildingSettings.homeownersDashboard.title")}</h3>
          <span className="text-xs text-gray-500">
            {t("buildingSettings.homeownersDashboard.summary", {
              householdsWithOwners,
              householdsCount: households.length,
            })}
            {totalHomeownerAccounts > 0 && (
              <span>
                {" "}
                {t("buildingSettings.homeownersDashboard.accountCount", {
                  count: totalHomeownerAccounts,
                })}
              </span>
            )}
          </span>
        </div>

        <div className="relative w-full lg:w-80">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("buildingSettings.homeownersDashboard.searchPlaceholder")}
            className="mt-0 h-8 rounded-md py-1 pl-9 shadow-none"
          />
        </div>
      </div>

      {removeError && (
        <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {removeError}
        </div>
      )}

      {passwordResetFeedback && (
        <div className="mx-4 mt-3">
          <Banner message={passwordResetFeedback.message} success={passwordResetFeedback.success} />
        </div>
      )}

      <div className="overflow-hidden">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500">
              <th className="w-24 px-4 py-2 sm:w-40">{t("buildingSettings.homeownersDashboard.apartment")}</th>
              <th className="px-4 py-2">{t("buildingSettings.homeownersDashboard.homeowners")}</th>
              <th className="w-40 px-4 py-2 text-right sm:w-72">{t("buildingSettings.homeownersDashboard.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredHouseholds.map((household) => {
              const accounts = getHouseholdAccounts(household);
              const isExpanded = expandedHouseholdId === household.id;

              return (
                <Fragment key={household.id}>
                  <tr className="border-b align-middle hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="text-sm font-medium text-gray-900">{household.title}</div>
                    </td>
                    <td className="px-4 py-2">
                      {accounts.length > 0 ? (
                        <div className="flex min-w-0 w-full flex-col gap-0.5">
                          {accounts.map((account) => (
                            <div
                              key={account.id}
                              className="grid min-w-0 w-full grid-cols-[minmax(0,10rem)_minmax(0,1fr)] items-center gap-2 rounded-full px-2 py-1 text-sm leading-5 transition-colors hover:bg-gray-200 sm:grid-cols-[minmax(8rem,16rem)_minmax(0,1fr)]"
                            >
                              <span className="truncate font-medium text-gray-900">{formatAccountName(account)}</span>
                              <span className="truncate text-gray-500">{formatAccountDetail(account, t)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          {t("buildingSettings.homeownersDashboard.noHomeowners")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-start justify-end gap-2">
                        {accounts.length > 0 && (
                          <div className="flex flex-col gap-0.5">
                            {accounts.map((account) => {
                              const passwordResetTarget = getPasswordResetTarget(account);
                              const isSendingPasswordReset =
                                passwordResetTarget?.userId === passwordResetMutation.variables?.userId &&
                                passwordResetMutation.isPending;
                              const passwordResetLabel = passwordResetTarget
                                ? t("buildingSettings.homeownersDashboard.sendPasswordResetEmailLabel", {
                                    email: passwordResetTarget.email,
                                  })
                                : undefined;

                              return (
                                <div key={account.id} className="flex h-7 items-center justify-end gap-1">
                                  {passwordResetTarget && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-7 gap-1 whitespace-nowrap px-2 shadow-none"
                                      aria-label={passwordResetLabel}
                                      title={passwordResetLabel}
                                      disabled={passwordResetMutation.isPending}
                                      loading={isSendingPasswordReset}
                                      onClick={() => passwordResetMutation.mutate(passwordResetTarget)}
                                    >
                                      {!isSendingPasswordReset && <EnvelopeIcon className="h-3.5 w-3.5" />}
                                      <span className="hidden sm:inline">
                                        {t("buildingSettings.homeownersDashboard.resetPassword")}
                                      </span>
                                    </Button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn-custom flex h-7 w-7 items-center justify-center rounded-md border border-transparent bg-transparent text-gray-300 hover:border-red-100 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-40"
                                    aria-label={t("buildingSettings.homeownersDashboard.removeOwnerLabel", {
                                      name: formatAccountName(account),
                                      apartment: household.title,
                                    })}
                                    disabled={removeMutation.isPending}
                                    onClick={() =>
                                      setPendingRemoval({
                                        householdId: household.id,
                                        householdTitle: household.title,
                                        account,
                                      })
                                    }
                                  >
                                    <span className="sr-only">
                                      {t("buildingSettings.homeownersDashboard.removeOwner")}
                                    </span>
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-1 whitespace-nowrap px-2 shadow-none"
                          onClick={() => setExpandedHouseholdId(isExpanded ? null : household.id)}
                        >
                          <UserPlusIcon className="h-3.5 w-3.5" />
                          {isExpanded
                            ? t("buildingSettings.homeownersDashboard.close")
                            : t("buildingSettings.homeownersDashboard.manage")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b bg-gray-50">
                      <td colSpan={3} className="px-4 py-3">
                        <UserManagement
                          entityId={household.id}
                          entityType={AssignmentEntityType.HOUSEHOLD}
                          compact
                          addOnly
                          onUpdate={() => handleHomeownerAdded(household.id)}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {filteredHouseholds.length === 0 && (
              <tr>
                <td colSpan={3} className="p-6 text-center text-sm text-gray-500">
                  {t("buildingSettings.homeownersDashboard.noResults")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmationDialog
        isOpen={pendingRemoval !== null}
        onCancel={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (!pendingRemoval) {
            return;
          }
          removeMutation.mutate({
            householdId: pendingRemoval.householdId,
            accountId: pendingRemoval.account.id,
          });
        }}
        title={t("buildingSettings.homeownersDashboard.confirmRemoveTitle")}
        message={t("buildingSettings.homeownersDashboard.confirmRemoveMessage", {
          name: pendingRemoval ? formatAccountName(pendingRemoval.account) : "",
          apartment: pendingRemoval?.householdTitle ?? "",
        })}
        confirmText={t("buildingSettings.homeownersDashboard.confirmRemove")}
        cancelText={t("common.cancel")}
        loading={removeMutation.isPending}
      />
    </section>
  );
}

function getHouseholdAccounts(household: HouseholdWithOwnership): Account[] {
  return (household.accounts ?? []).filter(Boolean);
}

function getAccountSearchText(account: Account): string {
  return [account.name, account.identifier, account.type].filter(Boolean).join(" ").toLowerCase();
}

function formatAccountName(account: Account): string {
  return account.name || account.identifier || `Account ${account.id}`;
}

function formatAccountDetail(account: Account, t: ReturnType<typeof useTranslation>["t"]): string {
  if (account.type === AccountType.INDIVIDUAL) {
    return account.identifier || t("buildingSettings.homeownersDashboard.individual");
  }
  return account.identifier || t("buildingSettings.homeownersDashboard.organization");
}

function getPasswordResetTarget(account: Account): { userId: number; email: string } | null {
  if (
    account.type !== AccountType.INDIVIDUAL ||
    account.primaryUserId === null ||
    account.primaryUserId === undefined ||
    !account.identifier
  ) {
    return null;
  }

  return {
    userId: account.primaryUserId,
    email: account.identifier,
  };
}

function unwrapApiResponse<T>(response: ApiResponse<T>): T {
  if (!response.success) {
    throw new Error(response.message || "Request failed");
  }

  return response.result;
}
